import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MDEditor from '@uiw/react-md-editor';
import { createBlog, updateBlog, getBlogById } from '../../services/blogService';
import { useAuth } from '../../contexts/AuthContext';
import MediaUploader from './MediaUploader';

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    category: '',
    tags: '',
    thumbnailUrl: '',
    content: '',
    metaTitle: '',
    metaDescription: '',
    canonical: '',
    slug: '',
    author: '',
    status: 'draft'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publishedBlog, setPublishedBlog] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      fetchBlog();
    }
  }, [id]);

  const fetchBlog = async () => {
    try {
      const blog = await getBlogById(id);
      setFormData({
        title: blog.title || '',
        summary: blog.summary || '',
        category: blog.category || '',
        tags: blog.tags ? blog.tags.join(', ') : '',
        thumbnailUrl: blog.thumbnailUrl || '',
        content: blog.content || '',
        metaTitle: blog.metaTitle || '',
        metaDescription: blog.metaDescription || '',
        canonical: blog.canonical || '',
        slug: blog.slug || '',
        author: blog.author || '',
        status: blog.status || 'draft'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setFetchLoading(false);
    }
  };

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'title') {
        newData.slug = slugify(value);
      }
      return newData;
    });
  };


  const handleThumbnailUpload = (url) => {
    setFormData(prev => ({ ...prev, thumbnailUrl: url }));
    setSuccess('Thumbnail uploaded successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSaveDraft = async () => {
    console.log('handleSaveDraft called');
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dataToSubmit = {
        ...formData,
        status: 'draft',
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        author: formData.author || user.email
      };
      console.log('Submitting draft data:', dataToSubmit);

      if (id) {
        console.log('Updating existing blog');
        await updateBlog(id, dataToSubmit);
        console.log('Draft updated successfully');
        setSuccess('Draft saved successfully');
      } else {
        console.log('Creating new blog');
        await createBlog(dataToSubmit);
        console.log('Draft created successfully');
        setSuccess('Draft created successfully');
      }
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving draft:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    console.log('handlePublish called');
    setSaving(true);
    setError(null);
    try {
      const dataToSubmit = {
        ...formData,
        status: 'published',
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        author: formData.author || user.email
      };
      console.log('Submitting publish data:', dataToSubmit);

      let blogData;
      if (id) {
        console.log('Updating existing blog for publish');
        await updateBlog(id, dataToSubmit);
        blogData = { ...dataToSubmit, id };
        console.log('Blog updated and published successfully');
      } else {
        console.log('Creating new blog for publish');
        const newBlogId = await createBlog(dataToSubmit);
        blogData = { ...dataToSubmit, id: newBlogId };
        console.log('Blog created and published successfully, ID:', newBlogId);
      }

      setPublishedBlog(blogData);
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error publishing blog:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.content || !formData.content.trim()) {
      setError('Content is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted with status:', formData.status);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed');

    if (formData.status === 'published') {
      console.log('Calling handlePublish');
      await handlePublish();
    } else {
      console.log('Calling handleSaveDraft');
      await handleSaveDraft();
    }
  };


  if (fetchLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">{id ? 'Edit Blog Post' : 'Create New Blog Post'}</h2>
        <div>
          <button
            type="button"
            className="btn btn-outline-secondary me-2"
            onClick={() => navigate('/admin/blogs')}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError(null)}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Main Content Section */}
        <div className="admin-form">
          <div className="form-section">
            <h3 className="section-title">Main Content</h3>
            <div className="row">
              <div className="col-md-8">
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="form-control"
                    required
                    placeholder="Enter blog post title"
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label">Slug</label>
                  <input
                    type="text"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="auto-generated-slug"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Summary</label>
              <textarea
                name="summary"
                value={formData.summary}
                onChange={handleChange}
                className="form-control"
                rows="3"
                placeholder="Brief summary of the blog post"
              />
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="">Select Category</option>
                    <option value="Technology">Technology</option>
                    <option value="Business">Business</option>
                    <option value="Lifestyle">Lifestyle</option>
                    <option value="Health">Health</option>
                    <option value="Education">Education</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Tags</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Author Name</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="form-control"
                placeholder="Enter author name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Content (Markdown) *</label>
              <MDEditor
                value={formData.content}
                onChange={(value) => setFormData(prev => ({ ...prev, content: value || '' }))}
                preview="edit"
                height={400}
              />
            </div>
          </div>

          {/* Media Section */}
          <div className="form-section">
            <h3 className="section-title">Media</h3>
            <div className="row">
              <div className="col-md-8">
                <div className="form-group">
                  <label className="form-label">Thumbnail URL</label>
                  <input
                    type="url"
                    name="thumbnailUrl"
                    value={formData.thumbnailUrl}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <MediaUploader onUpload={handleThumbnailUpload} />
              </div>
            </div>
            {formData.thumbnailUrl && (
              <div className="mt-3">
                <img
                  src={formData.thumbnailUrl}
                  alt="Thumbnail preview"
                  className="img-thumbnail"
                  style={{ maxWidth: '200px', maxHeight: '150px' }}
                />
              </div>
            )}
          </div>

          {/* SEO Section */}
          <div className="form-section">
            <h3 className="section-title">SEO Settings</h3>
            <div className="form-group">
              <label className="form-label">Meta Title</label>
              <input
                type="text"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleChange}
                className="form-control"
                placeholder="SEO title for search engines"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Meta Description</label>
              <textarea
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleChange}
                className="form-control"
                rows="2"
                placeholder="SEO description for search engines"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Canonical URL</label>
              <input
                type="url"
                name="canonical"
                value={formData.canonical}
                onChange={handleChange}
                className="form-control"
                placeholder="https://example.com/canonical-url"
              />
            </div>
          </div>

          {/* Status Section */}
          <div className="form-section">
            <h3 className="section-title">Publication Status</h3>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-3 mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleSaveDraft}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving Draft...
              </>
            ) : (
              'Save Draft'
            )}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                {formData.status === 'published' ? 'Publishing...' : 'Saving...'}
              </>
            ) : (
              formData.status === 'published' ? 'Publish' : 'Save'
            )}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      {showSuccessModal && publishedBlog && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-check-circle-fill me-2"></i>
                  Blog Published Successfully!
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowSuccessModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-check-circle text-success" style={{ fontSize: '2.5rem' }}></i>
                  </div>
                </div>
                <h6 className="text-center mb-3">{publishedBlog.title}</h6>
                <p className="text-muted text-center mb-4">
                  Your blog post has been published and is now live on your website.
                </p>
                <div className="d-flex justify-content-center gap-2">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => navigate(`/blog/${publishedBlog.slug}`)}
                  >
                    <i className="bi bi-eye me-2"></i>
                    View Blog Post
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/admin/blogs')}
                  >
                    <i className="bi bi-list me-2"></i>
                    Back to Blog List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBlogEditor;